import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AdminUser, type Role } from '../api';
import { Alert, Avatar, Badge, Button, Card, ConfirmDialog, EmptyState, Flash, PageHeader, Select, Spinner } from '../components/ui';
import { IconClose, IconSearch, IconUsers } from '../components/icons';

/**
 * Labels only. Which of these a given person may actually hand out comes from
 * the server, because the rule ("never above your own rank") has to hold there
 * whatever this screen decides to render.
 */
const ROLE_LABEL: Record<string, string> = {
  PENDING: 'No access yet',
  APPROVED_VIEWER: 'Approved requests viewer',
  REQUESTER: 'Requester',
  APPROVER: 'Approver',
  MTN_TECHNICIAN: 'MTN Technician',
  ADMIN: 'Administrator',
  SUPER_ADMIN: 'Super Admin',
  SYSTEM_ADMIN: 'System administrator',
};

/** One short line per role, shown under the picker in the edit dialog. */
const ROLE_DESCRIPTION: Record<string, string> = {
  PENDING: 'Sees only the waiting screen.',
  APPROVED_VIEWER: 'Can only read approved requests.',
  REQUESTER: 'Can raise and sign requests.',
  APPROVER: 'Can raise requests and approve other people’s.',
  MTN_TECHNICIAN: 'Can raise requests and work the IT queue.',
  ADMIN: 'IT queue, users, form settings, every request.',
  SUPER_ADMIN: 'Everything an administrator can, plus managing administrators and deleting records.',
  SYSTEM_ADMIN: 'The top tier, set in the server configuration.',
};

/** Most powerful first; people waiting for access go above all of them. */
const ROLE_ORDER: Role[] = ['PENDING', 'SYSTEM_ADMIN', 'SUPER_ADMIN', 'ADMIN', 'MTN_TECHNICIAN', 'APPROVER', 'REQUESTER', 'APPROVED_VIEWER'];

type Patch = Parameters<typeof api.updateUser>[1];

function when(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: 'medium' }) : 'Never';
}

/**
 * One table, everyone in it, the role changeable right on the row. People
 * waiting for access sit at the top, tinted, with "Choose a role" where
 * their role will be. Anything rarer (deactivating, self-approval, the
 * introduction, deleting) lives behind one Edit button per person.
 */
export function AdminUsers() {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ['admin', 'users'], queryFn: () => api.adminUsers() });
  const departments = useQuery({ queryKey: ['departments'], queryFn: () => api.departments() });

  const [query, setQuery] = useState('');
  const [showDeactivated, setShowDeactivated] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    void queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Patch; name: string }) => api.updateUser(id, patch),
    onSuccess: (_result, { patch, name }) => {
      refresh();
      if (patch.role) setFlash(patch.role === 'PENDING' ? `${name} no longer has a role.` : `${name} is now ${ROLE_LABEL[patch.role]}.`);
      else if (patch.isActive === false) setFlash(`${name} can no longer sign in. The account and its history stay.`);
      else if (patch.isActive === true) setFlash(`${name} can sign in again.`);
      else if (patch.selfApprove !== undefined) setFlash(`${name} ${patch.selfApprove ? 'may now' : 'may no longer'} approve their own requests.`);
      else if (patch.departmentId !== undefined) setFlash(`Department for ${name} updated.`);
    },
  });

  const showAgain = useMutation({
    mutationFn: ({ id }: { id: string; name: string }) => api.resetUserWalkthrough(id),
    onSuccess: (_result, { name }) => {
      refresh();
      setFlash(`${name} will see the introduction again next time they sign in.`);
    },
  });

  // Deleting an account: a reason, the address typed back, and a clear
  // statement of what it does and does not do, mirroring deleting a request.
  const [removing, setRemoving] = useState<AdminUser | null>(null);
  const [reason, setReason] = useState('');
  const [typedEmail, setTypedEmail] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);

  const remove = useMutation({
    mutationFn: (target: AdminUser) => api.deleteUser(target.id, { reason: reason.trim(), confirmEmail: typedEmail.trim() }),
    onSuccess: ({ email }) => {
      setRemoving(null);
      setEditingId(null);
      setReason('');
      setTypedEmail('');
      setFlash(`Removed ${email}. If they sign in again they arrive as a new account with no role.`);
      refresh();
    },
    onError: (err) => setRemoveError(err instanceof ApiError ? err.message : 'Could not remove the account.'),
  });

  const all = users.data?.users ?? [];
  const waiting = all.filter((user) => user.role === 'PENDING').length;
  const hidden = all.filter((user) => !user.isActive).length;
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return all
      .filter((user) => showDeactivated || user.isActive)
      .filter(
        (user) =>
          !needle ||
          user.displayName.toLowerCase().includes(needle) ||
          user.email.toLowerCase().includes(needle) ||
          (user.department?.name.toLowerCase().includes(needle) ?? false),
      )
      .sort((a, b) => ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role) || a.displayName.localeCompare(b.displayName));
  }, [all, query, showDeactivated]);

  const editing = all.find((user) => user.id === editingId) ?? null;
  const busy = update.isPending || showAgain.isPending || remove.isPending;

  if (users.isPending) return <Spinner label="Loading users" />;
  if (users.isError) return <ErrorNote error={users.error} />;

  const { assignableRoles, canDeleteUsers } = users.data;
  const departmentOptions = departments.data?.departments ?? [];
  const change = (user: AdminUser, patch: Patch) => update.mutate({ id: user.id, name: user.displayName, patch });

  return (
    <div>
      <PageHeader
        title="Users & roles"
        description="Everyone who has signed in. Pick a role from the list to give someone access."
        actions={
          waiting > 0 ? (
            <Badge tone="warning" dot>
              {waiting} waiting for a role
            </Badge>
          ) : undefined
        }
      />

      {update.isError && (
        <div className="mb-4">
          <ErrorNote error={update.error} />
        </div>
      )}
      {flash && (
        <div className="mb-4">
          <Flash message={flash} onDismiss={() => setFlash(null)} />
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        <label className="relative min-w-0 basis-full sm:max-w-sm sm:flex-1 sm:basis-auto">
          <IconSearch className="pointer-events-none absolute top-2.5 left-3 h-4 w-4 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search by name or email"
            aria-label="Search people"
            className="w-full rounded-lg border border-slate-300 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-400"
          />
        </label>
        {hidden > 0 && (
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700 select-none dark:text-slate-300">
            <input
              type="checkbox"
              checked={showDeactivated}
              onChange={(event) => setShowDeactivated(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300 accent-brand-600 dark:border-slate-600"
            />
            Show deactivated ({hidden})
          </label>
        )}
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={<IconUsers />}
          title={all.length === 0 ? 'Nobody has signed in yet' : 'Nobody matches'}
          body={all.length === 0 ? 'People appear here the first time they sign in.' : 'Try a shorter search.'}
        />
      ) : (
        <Card>
          {/* No minimum width: on a phone the person column gives way and the role picker stays usable. */}
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs font-medium tracking-wider text-slate-500 uppercase dark:border-slate-800 dark:text-slate-400">
                <th className="w-full px-4 py-3 sm:px-5">Person</th>
                <th className="px-3 py-3">Role</th>
                <th className="hidden px-3 py-3 md:table-cell">Department</th>
                <th className="hidden px-3 py-3 lg:table-cell">Last sign-in</th>
                <th className="px-4 py-3 sm:px-5">
                  <span className="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => {
                const pending = user.role === 'PENDING';
                return (
                  <tr
                    key={user.id}
                    data-pending={pending || undefined}
                    className={`border-b border-slate-100 last:border-b-0 dark:border-slate-800/70 ${
                      pending ? 'bg-amber-50/70 dark:bg-amber-950/20' : ''
                    } ${user.isActive ? '' : 'opacity-60'}`}
                  >
                    <td className="w-full max-w-0 px-4 py-3 sm:px-5">
                      <div className="flex items-center gap-3">
                        <span className="hidden sm:block">
                          <Avatar name={user.displayName} />
                        </span>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`truncate font-medium ${user.isActive ? 'text-slate-900 dark:text-white' : 'text-slate-500 line-through dark:text-slate-400'}`}>
                              {user.displayName}
                            </span>
                            {pending && (
                              <Badge tone="warning" dot>
                                Waiting
                              </Badge>
                            )}
                            {!user.isActive && <Badge tone="neutral">Deactivated</Badge>}
                          </div>
                          <div className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3">
                      <RolePicker user={user} assignableRoles={assignableRoles} disabled={busy} onChange={(role) => change(user, { role })} className="w-32 sm:w-52" />
                    </td>
                    <td className="hidden px-3 py-3 md:table-cell">
                      <Select
                        label={`Department for ${user.displayName}`}
                        value={user.department?.id ?? ''}
                        className="w-40"
                        disabled={busy}
                        onChange={(value) => change(user, { departmentId: value || null })}
                      >
                        <option value="">None</option>
                        {departmentOptions.map((department) => (
                          <option key={department.id} value={department.id}>
                            {department.name}
                          </option>
                        ))}
                      </Select>
                    </td>
                    <td className="hidden px-3 py-3 whitespace-nowrap text-slate-600 lg:table-cell dark:text-slate-400">{when(user.lastLoginAt)}</td>
                    <td className="px-4 py-3 text-right sm:px-5">
                      <Button size="sm" variant="secondary" onClick={() => setEditingId(user.id)} aria-label={`Edit ${user.displayName}`}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500 dark:text-slate-400">
        You cannot change your own role, hand out a role above your own, or edit a system administrator. MTN
        Technician can only be given or taken away by a system administrator.
      </p>

      {editing && (
        <EditDialog
          user={editing}
          departments={departmentOptions}
          assignableRoles={assignableRoles}
          canDelete={canDeleteUsers}
          busy={busy}
          onClose={() => setEditingId(null)}
          onChange={(patch) => change(editing, patch)}
          onShowAgain={() => showAgain.mutate({ id: editing.id, name: editing.displayName })}
          onDelete={() => {
            setRemoveError(null);
            setReason('');
            setTypedEmail('');
            setRemoving(editing);
          }}
        />
      )}

      {removing && (
        <ConfirmDialog
          title={`Remove ${removing.displayName}'s account?`}
          confirmLabel="Delete account"
          busy={remove.isPending}
          confirmDisabled={reason.trim().length < 10 || typedEmail.trim().toLowerCase() !== removing.email.toLowerCase()}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            setRemoveError(null);
            remove.mutate(removing);
          }}
          body={
            <>
              <p>
                This removes the account for {removing.email}: its role, department, grants and notifications. It has
                never been part of a request, so no record loses a name. Their sign-ins stay in the audit log, without
                the account attached.
              </p>
              <p className="mt-2">
                It does not touch Microsoft Entra. If the same person signs in again they arrive as a new account with no
                role. To keep somebody out without removing them, <strong>deactivate</strong> instead.
              </p>
              <label className="mt-4 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Why is it being removed?</span>
                <textarea
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={2}
                  placeholder="Signed in with the wrong account"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </label>
              <label className="mt-3 block">
                <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                  Type <code>{removing.email}</code> to confirm
                </span>
                <input
                  value={typedEmail}
                  onChange={(event) => setTypedEmail(event.target.value)}
                  autoComplete="off"
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                />
              </label>
              {removeError && (
                <div className="mt-3">
                  <Alert>{removeError}</Alert>
                </div>
              )}
            </>
          }
        />
      )}
    </div>
  );
}

/**
 * The role select, shared by the row and the dialog. Someone waiting sees
 * "Choose a role" and a highlighted border; a role the viewer may not hand
 * out is listed but greyed, so the ladder is visible without being usable.
 */
function RolePicker({
  user,
  assignableRoles,
  disabled,
  onChange,
  className = 'w-52',
}: {
  user: AdminUser;
  assignableRoles: Role[];
  disabled: boolean;
  onChange: (role: Role) => void;
  className?: string;
}) {
  const pending = user.role === 'PENDING';
  // The server refuses these too; this only stops the picker offering what will bounce.
  const locked = !user.manageable || (user.role === 'MTN_TECHNICIAN' && !assignableRoles.includes('MTN_TECHNICIAN'));
  const offered = ROLE_ORDER.filter((role) => role === user.role || (role !== 'PENDING' && assignableRoles.includes(role)));
  if (locked) {
    return <span className="text-slate-700 dark:text-slate-300">{ROLE_LABEL[user.role] ?? user.role}</span>;
  }
  return (
    <Select
      label={`Role for ${user.displayName}`}
      value={user.role}
      disabled={disabled}
      className={`${className} ${pending ? 'border-amber-400 font-medium dark:border-amber-600' : ''}`}
      onChange={(value) => value !== user.role && onChange(value as Role)}
    >
      {offered.map((role) => (
        <option key={role} value={role} disabled={role !== user.role && !assignableRoles.includes(role)}>
          {role === 'PENDING' ? 'Choose a role…' : ROLE_LABEL[role]}
        </option>
      ))}
    </Select>
  );
}

/** The rarer controls for one person, in a small dialog. Each change applies at once. */
function EditDialog({
  user,
  departments,
  assignableRoles,
  canDelete,
  busy,
  onClose,
  onChange,
  onShowAgain,
  onDelete,
}: {
  user: AdminUser;
  departments: { id: string; name: string }[];
  assignableRoles: Role[];
  canDelete: boolean;
  busy: boolean;
  onClose: () => void;
  onChange: (patch: Patch) => void;
  onShowAgain: () => void;
  onDelete: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const locked = !user.manageable;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center p-4 sm:items-center" role="dialog" aria-modal="true" aria-label={`Edit ${user.displayName}`}>
      <div className="animate-fade absolute inset-0 bg-slate-950/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div className="animate-rise relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-950">
        <div className="flex items-start gap-3">
          <Avatar name={user.displayName} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-base font-semibold text-slate-900 dark:text-white">{user.displayName}</h2>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">{user.email}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mt-1 -mr-2 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <IconClose className="h-5 w-5" />
          </button>
        </div>

        {locked && (
          <div className="mt-4">
            <Alert tone="info">
              {user.role === 'SYSTEM_ADMIN'
                ? 'A system administrator, set in the server configuration. Nobody can change this account here.'
                : 'This account is at or above your own level, so you can see it but not change it.'}
            </Alert>
          </div>
        )}

        <div className="mt-5 space-y-5">
          <label className="block">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Role</span>
            <div className="mt-1.5">
              <RolePicker user={user} assignableRoles={assignableRoles} disabled={busy} onChange={(role) => onChange({ role })} className="w-full" />
            </div>
            <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">{ROLE_DESCRIPTION[user.role]}</span>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-slate-800 dark:text-slate-200">Department</span>
            <div className="mt-1.5">
              <Select
                label={`Department for ${user.displayName}`}
                value={user.department?.id ?? ''}
                className="w-full"
                disabled={busy}
                onChange={(value) => onChange({ departmentId: value || null })}
              >
                <option value="">None</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </Select>
            </div>
          </label>

          <CheckRow
            checked={user.isActive}
            disabled={busy || locked}
            onChange={(next) => onChange({ isActive: next })}
            label="Can sign in"
            hint="Untick to deactivate. The account and everything it did stay on record."
          />
          <CheckRow
            checked={user.selfApprove}
            disabled={busy || locked}
            onChange={(next) => onChange({ selfApprove: next })}
            label="May approve their own requests"
            hint="Off for everyone unless you tick it. Every use is written on the request."
          />

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm dark:border-slate-800">
            <span className="text-slate-600 dark:text-slate-400">
              Last signed in {when(user.lastLoginAt).toLowerCase()}.{user.historyNote ? ` ${user.historyNote}` : ''}
            </span>
            {!locked && user.walkthroughSeenAt && (
              <Button size="sm" variant="ghost" disabled={busy} onClick={onShowAgain}>
                Show the introduction again
              </Button>
            )}
          </div>

          {canDelete && !locked && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 text-sm dark:border-slate-800">
              <span className="text-slate-600 dark:text-slate-400">
                {user.deletable ? 'No history yet, so the account can be deleted outright.' : 'Named on records, so it cannot be deleted. Deactivate instead.'}
              </span>
              {user.deletable && (
                <Button size="sm" variant="ghost" disabled={busy} onClick={onDelete} className="text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40">
                  Delete account
                </Button>
              )}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}

function CheckRow({
  checked,
  disabled,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-brand-600 dark:border-slate-600"
      />
      <span className="min-w-0">
        <span className="block text-sm font-medium text-slate-800 dark:text-slate-200">{label}</span>
        <span className="block text-xs leading-relaxed text-slate-500 dark:text-slate-400">{hint}</span>
      </span>
    </label>
  );
}

function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof ApiError ? error.message : error instanceof Error ? error.message : 'Something went wrong.';
  return <Alert>{message}</Alert>;
}
